/**
 * Public Euler Route Specifications
 *
 * Single source of truth for all public Euler API routes.
 * Defines Zod schemas, upstream path builders, cache policies,
 * and client URL builders for each route.
 *
 * Server-side route handlers use these specs to validate requests
 * and build bounded upstream calls. Client-side hooks use the
 * clientUrl builders to generate fetch URLs.
 */

import { z } from 'zod'
import { getEulerSupportedChainIds } from '@/config/euler-contracts'

// =============================================================================
// Shared Schemas
// =============================================================================

/** Validates chainId against dynamically supported Euler chains */
export const eulerChainIdSchema = z.string().refine(
  (v) => getEulerSupportedChainIds().includes(Number(v)),
  { message: 'Unsupported Euler chain ID' },
)

/** Validates numeric chainId against dynamically supported Euler chains */
export const eulerChainIdNumberSchema = z.number().int().refine(
  (v) => getEulerSupportedChainIds().includes(v),
  { message: 'Unsupported Euler chain ID' },
)

/** Validates a hex address (0x-prefixed, 40 hex chars) */
const hexAddressSchema = z.string().regex(/^0x[a-fA-F0-9]{40}$/, {
  message: 'Invalid hex address',
})

/** Wallet-related fields to strip from POST bodies (not reject) */
const WALLET_FIELDS_TO_STRIP = ['inWallet', 'onlyInWallet'] as const

// =============================================================================
// Route Spec Types
// =============================================================================

export interface UpstreamGetRequest {
  path: string
  query: Record<string, string>
}

export interface UpstreamPostRequest {
  path: string
  query: Record<string, string>
  body: Record<string, unknown>
}

// =============================================================================
// Vault List Body Schema (POST /v2/vault/list)
// =============================================================================

const vaultListBodySchema = z.object({
  chainId: eulerChainIdNumberSchema,
  search: z.string().optional(),
  assets: z.array(z.string()).optional(),
  vaults: z.array(z.string()).optional(),
  products: z.array(z.string()).optional(),
  entities: z.array(z.string()).optional(),
  settings: z.object({
    disableIntrinsicApy: z.boolean().optional(),
    disableRewardsApy: z.boolean().optional(),
    enabledPerspectives: z.array(z.string()).optional(),
    disabledPerspectives: z.array(z.string()).optional(),
  }).optional(),
  utilizationMin: z.string().optional(),
  utilizationMax: z.string().optional(),
  orderBy: z.enum(['vault', 'asset', 'assetSymbol', 'apy', 'utilization', 'totalSupply']).optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  page: z.string().optional(),
  limit: z.string().optional(),
}).strict()

/** Schema for vault/assets POST body — accepts chainId plus optional filters */
const vaultAssetsBodySchema = z.object({
  chainId: eulerChainIdNumberSchema,
}).strict()

/** Schema for vault/strategies POST body — accepts chainId plus optional filters */
const vaultStrategiesBodySchema = z.object({
  chainId: eulerChainIdNumberSchema,
}).strict()

// =============================================================================
// Public Route Definitions
// =============================================================================

export const publicEulerRoutes = {
  prices: {
    method: 'GET' as const,
    query: z.object({ chainId: eulerChainIdSchema }).strict(),
    toUpstream: ({ chainId }: { chainId: string }): UpstreamGetRequest => ({
      path: '/v1/prices',
      query: { chainId },
    }),
    cacheControl: 'public, s-maxage=30, stale-while-revalidate=300',
    clientUrl: (chainId: number) => `/api/euler/prices?chainId=${chainId}`,
  },

  tokens: {
    method: 'GET' as const,
    query: z.object({ chainId: eulerChainIdSchema }).strict(),
    toUpstream: ({ chainId }: { chainId: string }): UpstreamGetRequest => ({
      path: '/v1/tokens',
      query: { chainId },
    }),
    cacheControl: 'public, s-maxage=300, stale-while-revalidate=3600',
    clientUrl: (chainId: number) => `/api/euler/tokens?chainId=${chainId}`,
  },

  intrinsicApyLatest: {
    method: 'GET' as const,
    query: z.object({ chainId: eulerChainIdSchema }).strict(),
    toUpstream: ({ chainId }: { chainId: string }): UpstreamGetRequest => ({
      path: '/v1/intrinsic-apy/latest',
      query: { chainId },
    }),
    cacheControl: 'public, s-maxage=60, stale-while-revalidate=300',
    clientUrl: (chainId: number) => `/api/euler/intrinsic-apy/latest?chainId=${chainId}`,
  },

  earnVaults: {
    method: 'GET' as const,
    query: z.object({ chainId: eulerChainIdSchema }).strict(),
    // Server hardcodes take/skip — client does not control pagination
    toUpstream: ({ chainId }: { chainId: string }): UpstreamGetRequest => ({
      path: '/v1/earn/vaults',
      query: { chainId, take: '10000', skip: '0' },
    }),
    cacheControl: 'public, s-maxage=60, stale-while-revalidate=300',
    clientUrl: (chainId: number) => `/api/euler/earn/vaults?chainId=${chainId}`,
  },

  earnVault: {
    method: 'GET' as const,
    query: z.object({
      chainId: eulerChainIdSchema,
      vaultAddress: hexAddressSchema,
    }).strict(),
    toUpstream: ({ chainId, vaultAddress }: { chainId: string; vaultAddress: string }): UpstreamGetRequest => ({
      path: '/v1/earn/vault',
      query: { chainId, vaultAddress },
    }),
    cacheControl: 'public, s-maxage=60, stale-while-revalidate=300',
    clientUrl: (chainId: number, { vaultAddress }: { vaultAddress: string }) =>
      `/api/euler/earn/vault?chainId=${chainId}&vaultAddress=${vaultAddress}`,
  },

  vaultList: {
    method: 'POST' as const,
    query: z.object({ chainId: eulerChainIdSchema }).strict(),
    body: vaultListBodySchema,
    toUpstream: ({ chainId }: { chainId: string }, body: z.infer<typeof vaultListBodySchema>): UpstreamPostRequest => ({
      path: '/v2/vault/list',
      query: { chainId },
      body,
    }),
    cacheControl: 'private, no-store',
    clientUrl: (chainId: number) => `/api/euler/vault/list?chainId=${chainId}`,
  },

  vaultAssets: {
    method: 'POST' as const,
    query: z.object({ chainId: eulerChainIdSchema }).strict(),
    body: vaultAssetsBodySchema,
    toUpstream: ({ chainId }: { chainId: string }, body: z.infer<typeof vaultAssetsBodySchema>): UpstreamPostRequest => ({
      path: '/v2/vault/assets',
      query: { chainId },
      body,
    }),
    cacheControl: 'public, s-maxage=60, stale-while-revalidate=300',
    clientUrl: (chainId: number) => `/api/euler/vault/assets?chainId=${chainId}`,
  },

  vaultStrategies: {
    method: 'POST' as const,
    query: z.object({ chainId: eulerChainIdSchema }).strict(),
    body: vaultStrategiesBodySchema,
    toUpstream: ({ chainId }: { chainId: string }, body: z.infer<typeof vaultStrategiesBodySchema>): UpstreamPostRequest => ({
      path: '/v2/vault/strategies',
      query: { chainId },
      body,
    }),
    cacheControl: 'public, s-maxage=60, stale-while-revalidate=300',
    clientUrl: (chainId: number) => `/api/euler/vault/strategies?chainId=${chainId}`,
  },
} as const

export type PublicEulerRouteKey = keyof typeof publicEulerRoutes
export type GetRouteKey = { [K in PublicEulerRouteKey]: (typeof publicEulerRoutes)[K]['method'] extends 'GET' ? K : never }[PublicEulerRouteKey]
export type PostRouteKey = { [K in PublicEulerRouteKey]: (typeof publicEulerRoutes)[K]['method'] extends 'POST' ? K : never }[PublicEulerRouteKey]

// =============================================================================
// Parsing Helpers
// =============================================================================

/**
 * Parse and validate query params for a GET route.
 * Rejects undeclared params via .strict().
 */
export function parseGetRouteQuery<K extends GetRouteKey>(
  routeKey: K,
  params: Record<string, string>,
): z.infer<(typeof publicEulerRoutes)[K]['query']> {
  const spec = publicEulerRoutes[routeKey]
  return spec.query.parse(params) as z.infer<(typeof publicEulerRoutes)[K]['query']>
}

/**
 * Parse and validate a POST route body.
 * Strips wallet-related fields before validation.
 */
export function parsePostRouteBody<K extends PostRouteKey>(
  routeKey: K,
  body: unknown,
): z.infer<(typeof publicEulerRoutes)[K]['body']> {
  // Strip wallet fields before validation
  const sanitized =
    body && typeof body === 'object' && !Array.isArray(body)
      ? { ...body as Record<string, unknown> }
      : body

  if (sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized)) {
    for (const field of WALLET_FIELDS_TO_STRIP) {
      delete (sanitized as Record<string, unknown>)[field]
    }
  }

  const spec = publicEulerRoutes[routeKey]
  return (spec as { body: z.ZodTypeAny }).body.parse(sanitized) as z.infer<(typeof publicEulerRoutes)[K]['body']>
}

/**
 * Parse query params for a POST route (chainId from URL).
 */
export function parsePostRouteQuery<K extends PostRouteKey>(
  routeKey: K,
  params: Record<string, string>,
): z.infer<(typeof publicEulerRoutes)[K]['query']> {
  const spec = publicEulerRoutes[routeKey]
  return spec.query.parse(params) as z.infer<(typeof publicEulerRoutes)[K]['query']>
}
